/**
 * Per-page document titles (WCAG 2.4.2 Page Titled). Before this every page was titled
 * "<Company> - Goodstuff Portal", so browser tabs, history and the page-change announcement that
 * screen readers make all said the same thing.
 *
 * Title = "<page> – <base>". The base is the company/brand title CompanyContext sets (setBaseTitle).
 * The page part comes from the URL for customer and public pages (record numbers included), and
 * from the page heading in the innermost <main> for admin pages, whose headings already name the
 * page and record ("Users", "Beta Trading"), suffixed with the admin area.
 */
import { slugFromPathname } from './paths';

const AREAS: Record<string, string> = { glbadm: 'Global Admin', rsladm: 'Reseller Admin', tntadm: 'Tenant Admin', admin: 'Admin' };

const TOP: Record<string, string> = {
  '': 'Dashboard', login: 'Sign in', dashboard: 'Dashboard', 'reset-password': 'Reset password', 'set-password': 'Set password',
  'access-request': 'Request access', 'reseller-register': 'Reseller application', downloads: 'Downloads',
  'docs/setup': 'Setup guide', help: 'Help', 'legal/privacy-policy': 'Privacy policy', 'legal/terms-of-service': 'Terms of service',
  'legal/acceptable-use-policy': 'Acceptable use policy', 'legal/data-processing-agreement': 'Data processing agreement',
  'legal/service-level-agreement': 'Service level agreement', billing: 'Billing', 'shell-preview': 'Shell preview',
};

const NEW: Record<string, string> = { orders: 'New order', quotes: 'New quote' };
const RECORD: Record<string, string> = { orders: 'Order', quotes: 'Quote', invoices: 'Invoice', 'credit-memos': 'Credit memo', shipments: 'Shipment' };
const SLUG_PAGE: Record<string, string> = {
  '': 'Dashboard', login: 'Sign in', settings: 'Settings', 'my-account': 'My account', support: 'Support tickets',
  'order-form': 'New order', 'credit-memo': 'Credit memo', shipment: 'Shipment',
};

const decode = (s: string): string => { try { return decodeURIComponent(s); } catch { return s; } };

export interface PathTitle {
  /** Page name known from the URL alone; null when the page heading should be used. */
  page: string | null;
  /** Admin area name appended after the page, when on an admin route. */
  area: string | null;
}

export function titleForPath(pathname: string): PathTitle {
  const parts = pathname.split('/').filter(Boolean).map(decode);
  const first = (parts[0] ?? '').toLowerCase();
  if (AREAS[first]) { return { page: null, area: AREAS[first] }; }
  if (first === 'tickets' && parts[1]) { return { page: `Ticket ${parts[1]}`, area: null }; }
  const top = TOP[parts.join('/').toLowerCase()];
  if (top) { return { page: top, area: null }; }
  if (!slugFromPathname(pathname)) { return { page: null, area: null }; }
  const [kind = '', id] = parts.slice(1);
  const k = kind.toLowerCase();
  if (parts.length === 3 && id) {
    if (id.toLowerCase() === 'new' && NEW[k]) { return { page: NEW[k], area: null }; }
    if (RECORD[k]) { return { page: `${RECORD[k]} ${id}`, area: null }; }
  }
  if (parts.length <= 2 && SLUG_PAGE[k] !== undefined) { return { page: SLUG_PAGE[k], area: null }; }
  return { page: null, area: null };
}

/** Text of the first h1/h2 in the innermost main, without leading icons or a trailing "(count)". */
export function headingText(doc: Document = document): string | null {
  const mains = doc.querySelectorAll<HTMLElement>('main, [role=main]');
  const main = mains.length ? mains[mains.length - 1] : null;
  const raw = main?.querySelector('h1, h2')?.textContent ?? '';
  const text = raw.replace(/\s+/g, ' ').replace(/^[^\p{L}\p{N}]+/u, '').replace(/\s*\(\d[\d,]*\)\s*$/, '').trim();
  return text ? (text.length > 60 ? `${text.slice(0, 59)}…` : text) : null;
}

let base = typeof document !== 'undefined' && document.title ? document.title : 'Goodstuff Portal';
let page: string | null = null;

function apply(): void { document.title = page ? `${page} – ${base}` : base; }

/** CompanyContext calls this instead of writing document.title, so the page part survives. */
export function setBaseTitle(next: string): void { base = next; apply(); }

let seq = 0;
/** Recompute the page part for the current URL. Heading-driven pages get a short grace period to render. */
export function syncPageTitle(pathname: string = window.location.pathname): void {
  const run = ++seq;
  const { page: fromPath, area } = titleForPath(pathname);
  const join = (p: string | null): string | null => [p, area].filter(Boolean).join(' – ') || null;
  if (fromPath) { page = join(fromPath); apply(); return; }
  page = join(null); apply();
  const tryHeading = (attempt: number): void => {
    if (run !== seq) { return; } // a newer navigation took over
    const h = headingText();
    if (h) { page = join(h); apply(); return; }
    if (attempt < 15) { setTimeout(() => tryHeading(attempt + 1), 100); }
  };
  tryHeading(0);
}

interface NavInfo { pathChanged: boolean; fromLocation?: unknown; toLocation?: { pathname: string } }
type Subscribe = (event: 'onRendered', fn: (e: NavInfo) => void) => () => void;

export function installPageTitle(router: { subscribe: Subscribe }): () => void {
  return router.subscribe('onRendered', (e) => {
    if (e.fromLocation && !e.pathChanged) { return; } // only ?search/#hash changed
    requestAnimationFrame(() => syncPageTitle(e.toLocation?.pathname));
  });
}

/** Test hook. */
export function resetPageTitleForTests(nextBase = 'Goodstuff Portal'): void { base = nextBase; page = null; seq++; }
